// CMS media — uploads, listing, and public URLs.
// Bucket: `cms-media` (public — gated on write by storage.objects RLS)
// Path convention: {organization_id}/{random_id}.{ext}
//
// Filename is prefixed by a UUID so two files with the same name can
// coexist. The original filename is tracked in a JSON metadata column
// at upload time (via `fileName` option) so the editor can reference
// it later for alt text.

import { supabase, isSupabaseConfigured } from "../supabase";

const BUCKET = "cms-media";

function ensure() {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
}

function randomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export async function listMedia(organizationId) {
  ensure();
  if (!organizationId) return [];
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .list(organizationId, {
      limit: 200,
      sortBy: { column: "created_at", order: "desc" },
    });
  if (error) throw error;
  // Supabase returns bookkeeping `.emptyFolderPlaceholder` entries — skip them.
  return (data || []).filter((f) => f.name && !f.name.endsWith("/") && f.metadata);
}

export async function uploadMedia({ organizationId, file }) {
  ensure();
  if (!organizationId) throw new Error("No organization context");
  if (!file) throw new Error("No file selected");
  if (file.size > 10 * 1024 * 1024) {
    throw new Error("File too large (max 10MB)");
  }
  const ext = (file.name.split(".").pop() || "bin").toLowerCase();
  const path = `${organizationId}/${randomId()}.${ext}`;
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, {
      contentType: file.type || "application/octet-stream",
      upsert: false,
      // Store the original filename on the object metadata so the editor
      // can use it later as the default alt text.
      metadata: { original_name: file.name },
    });
  if (error) throw error;
  return { path, originalName: file.name };
}

/**
 * For a public bucket, `getPublicUrl` returns a URL that works
 * indefinitely — we use this for both preview thumbnails AND for
 * image URLs pasted into CMS markdown (so they don't expire).
 */
export function mediaPublicUrl(organizationId, fileName) {
  const { data } = supabase.storage
    .from(BUCKET)
    .getPublicUrl(`${organizationId}/${fileName}`);
  return data?.publicUrl || null;
}

export async function deleteMedia(organizationId, fileName) {
  ensure();
  const { error } = await supabase.storage
    .from(BUCKET)
    .remove([`${organizationId}/${fileName}`]);
  if (error) throw error;
}

export function isImage(name) {
  return /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(name || "");
}

export function humanSize(bytes) {
  if (!Number.isFinite(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
